/**
 * THE FORM MODEL, AND THE VALIDATION THAT MIRRORS THE API.
 *
 * The draft the person is editing is a flat object of plain strings and small arrays; the PATCH
 * body is derived from it. Keeping the two apart matters because the contract's semantics are
 * about ABSENCE: "a property that is absent is left alone, and a nullable property sent as `null`
 * clears the value". A form that sent every field every time would clear things the person never
 * touched, so `toUpdateBody` sends only what changed and turns an emptied field into an explicit
 * `null`.
 *
 * VALIDATION IS A MIRROR, NOT A GATE. The server is the authority and its 422 is the real check.
 * Everything here exists so the person is told before they press a button, and every rule is
 * copied from a published constraint in `authors-api.yaml` — nothing invented, nothing stricter.
 * Error keys are DOT-NOTATION PATHS matching the field names, so a server 422 can be merged into
 * the same map with no translation layer.
 */
import type {
  AuthorTag,
  ExpertiseEntry,
  FieldVisibility,
  OwnProfile,
  OwnProfileUpdate,
  PrimaryLocation,
  SocialLink,
} from '../contract';
import type { TFunction } from 'i18next';
import { i18n } from '../../../i18n';
import { LIMITS, SLUG_SHAPE, YEARS_OF_PRACTICE, codePointLength, graphemeLength } from './limits';

/**
 * "Primary location" in the form (design 25; design 24, T27): what is in the box, and the place
 * PICKED from the town search - only a pick is ever sent. Editing the text un-picks it; an empty
 * box clears the location; text that was not picked is refused on screen and sent as nothing.
 */
export interface PrimaryLocationDraft {
  readonly text: string;
  readonly picked: PrimaryLocation | null;
}

/** The editable shape. Text fields are strings, never null, so an input is always controlled. */
export interface ApplicationDraft {
  displayName: string;
  slug: string;
  headline: string;
  bioLong: string;
  bioShort: string;
  about: string;
  experience: string;
  approach: string;
  education: string;
  pronouns: string;
  /** No longer a field (design 25): kept so a save never sends it; a pick sets it on the server. */
  timezone: string;
  primaryLocation: PrimaryLocationDraft;
  /** "Years of practice", as typed: a whole number 0 to 80, or empty. */
  yearsOfPractice: string;
  contactFormEnabled: boolean;
  visibility: FieldVisibility;
  expertise: readonly ExpertiseEntry[];
  tags: readonly AuthorTag[];
  links: readonly SocialLink[];
  privateEmail: string;
  availabilityNote: string;
}

export type DraftKey = keyof ApplicationDraft;

/** Errors keyed by the same names the fields use, so a 422 merges straight in. */
export type FieldErrors = Readonly<Partial<Record<string, string>>>;

function text(value: string | null | undefined): string {
  return value ?? '';
}

/** The server's profile becomes the draft. Nulls become empty strings; nothing else changes. */
export function draftFromProfile(profile: OwnProfile): ApplicationDraft {
  return {
    displayName: text(profile.displayName),
    slug: text(profile.slug),
    headline: text(profile.headline),
    bioLong: text(profile.bioLong),
    bioShort: text(profile.bioShort),
    about: text(profile.about),
    experience: text(profile.experience),
    approach: text(profile.approach),
    education: text(profile.education),
    pronouns: text(profile.pronouns),
    timezone: text(profile.timezone),
    primaryLocation: locationDraftOf(profile.primaryLocation),
    yearsOfPractice:
      profile.yearsOfPractice === null || profile.yearsOfPractice === undefined
        ? ''
        : String(profile.yearsOfPractice),
    contactFormEnabled: profile.contactFormEnabled ?? false,
    visibility: profile.visibility,
    expertise: profile.expertise,
    tags: profile.tags,
    links: profile.links,
    privateEmail: text(profile.private?.email),
    availabilityNote: text(profile.private?.availabilityNote),
  };
}

/** Which of the six sections a key belongs to, so a section can be saved on its own. */
export const SECTION_KEYS = {
  identity: ['displayName', 'headline', 'slug', 'pronouns', 'timezone', 'primaryLocation'],
  about: ['bioLong', 'bioShort', 'about', 'experience', 'approach', 'education'],
  expertise: ['expertise', 'tags', 'yearsOfPractice'],
  photograph: [],
  links: ['links'],
  private: ['privateEmail', 'availabilityNote', 'contactFormEnabled'],
} as const satisfies Record<string, readonly DraftKey[]>;

export type SectionId = keyof typeof SECTION_KEYS;

function sameArray<T>(a: readonly T[], b: readonly T[]): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * The PATCH body for the keys that actually changed.
 *
 * An emptied text field becomes explicit `null` (clear it); an unchanged one is absent (leave it
 * alone). The three arrays are sent WHOLE — that is the replacement reading recorded in
 * `limits.ts`, and it is the only reading under which an author can remove a link at all, since
 * the surface has no per-item delete route.
 */
export function toUpdateBody(
  draft: ApplicationDraft,
  baseline: ApplicationDraft,
  keys: readonly DraftKey[],
): OwnProfileUpdate {
  const body: Record<string, unknown> = {};
  const nullable = (value: string): string | null => (value.trim().length === 0 ? null : value);
  const privateBlock: Record<string, unknown> = {};

  for (const key of keys) {
    switch (key) {
      case 'displayName':
      case 'slug':
      case 'headline':
      case 'bioLong':
      case 'bioShort':
      case 'about':
      case 'experience':
      case 'approach':
      case 'education':
      case 'pronouns':
      case 'timezone':
        if (draft[key] !== baseline[key]) body[key] = nullable(draft[key]);
        break;
      case 'primaryLocation': {
        // Only a pick, or a clear, is sent: text not picked is no change (and refused on screen).
        const next = locationToSend(draft.primaryLocation);
        if (next !== undefined && !sameLocation(next, locationToSend(baseline.primaryLocation))) {
          body.primaryLocation = next;
        }
        break;
      }
      case 'yearsOfPractice':
        if (draft.yearsOfPractice.trim() !== baseline.yearsOfPractice.trim()) {
          const typed = draft.yearsOfPractice.trim();
          body.yearsOfPractice = typed === '' ? null : Number(typed);
        }
        break;
      case 'contactFormEnabled':
        if (draft.contactFormEnabled !== baseline.contactFormEnabled) {
          body.contactFormEnabled = draft.contactFormEnabled;
        }
        break;
      case 'visibility':
        if (JSON.stringify(draft.visibility) !== JSON.stringify(baseline.visibility)) {
          body.visibility = draft.visibility;
        }
        break;
      case 'expertise':
        // Main areas are always public (the owner, 2026-10-19): an area saved private before has
        // no switch to turn it back on, so every save sends it public.
        if (!sameArray(draft.expertise, baseline.expertise)) {
          body.expertise = draft.expertise.map((entry) => ({ ...entry, isPublic: true }));
        }
        break;
      case 'tags':
        if (!sameArray(draft.tags, baseline.tags)) body.tags = draft.tags;
        break;
      case 'links':
        if (!sameArray(draft.links, baseline.links)) body.links = draft.links;
        break;
      case 'privateEmail':
        if (draft.privateEmail !== baseline.privateEmail) {
          privateBlock.email = nullable(draft.privateEmail);
        }
        break;
      case 'availabilityNote':
        if (draft.availabilityNote !== baseline.availabilityNote) {
          privateBlock.availabilityNote = nullable(draft.availabilityNote);
        }
        break;
      default:
        break;
    }
  }

  if (Object.keys(privateBlock).length > 0) body.private = privateBlock;
  return body as OwnProfileUpdate;
}

/** True when nothing in `keys` differs from what the server last returned. */
export function isUnchanged(
  draft: ApplicationDraft,
  baseline: ApplicationDraft,
  keys: readonly DraftKey[],
): boolean {
  return Object.keys(toUpdateBody(draft, baseline, keys)).length === 0;
}

interface Bound {
  readonly min?: number | undefined;
  readonly max?: number | undefined;
}

/**
 * The two-count check. The published number is a GRAPHEME count and is what the person is shown;
 * `maxLength` counts code points and, where they differ, IS THE STRICTER CHECK. So a value can be
 * inside its published limit and still be refused, and the only honest client-side mirror tests
 * both.
 */
function lengthProblem(
  value: string,
  label: string,
  bound: Bound,
  t: TFunction<'application'>,
): string | undefined {
  const graphemes = graphemeLength(value);
  const codePoints = codePointLength(value);
  if (bound.max !== undefined) {
    if (graphemes > bound.max)
      return t('validation.tooLong', { label, count: graphemes, max: bound.max });
    if (codePoints > bound.max) {
      return t('validation.storedAsSeveral', { label, max: bound.max });
    }
  }
  if (bound.min !== undefined && value.length > 0 && graphemes < bound.min) {
    return t('validation.tooShort', { label, count: graphemes, min: bound.min });
  }
  return undefined;
}

/**
 * Validates the draft against the published constraints. `slugEditable` is false once the author
 * is approved — the address is frozen server-side and the field is read-only, so it is not checked.
 */
export function validateDraft(
  draft: ApplicationDraft,
  slugEditable: boolean,
  // The form passes its own `t`; anything else reads the catalogue in the language now in force.
  t: TFunction<'application'> = i18n.getFixedT(null, 'application'),
): FieldErrors {
  const errors: Record<string, string> = {};

  const put = (key: string, message: string | undefined): void => {
    if (message !== undefined) errors[key] = message;
  };

  // displayName publishes no maxLength on OwnProfileUpdate — see the findings. Nothing to check
  // here beyond the completeness rule, which is the server's, so no invented limit goes on screen.

  if (slugEditable && draft.slug.length > 0) {
    const slug = draft.slug;
    if (slug.length < LIMITS.slug.min || slug.length > LIMITS.slug.max) {
      put(
        'slug',
        t('validation.slug.length', {
          count: slug.length,
          min: LIMITS.slug.min,
          max: LIMITS.slug.max,
        }),
      );
    } else if (!SLUG_SHAPE.test(slug)) {
      put('slug', t('validation.slug.shape'));
    }
  }

  const length = (key: DraftKey & keyof typeof LENGTH_LABEL, bound: Bound): void => {
    put(key, lengthProblem(draft[key], t(LENGTH_LABEL[key]), bound, t));
  };
  length('headline', LIMITS.headline);
  length('bioShort', LIMITS.bioShort);
  length('about', LIMITS.about);
  length('experience', LIMITS.experience);
  length('approach', LIMITS.approach);
  length('education', LIMITS.education);
  length('pronouns', LIMITS.pronouns);
  length('privateEmail', LIMITS.privateEmail);
  length('availabilityNote', LIMITS.availabilityNote);

  // The bio is a BAND and the minimum is real: 600 to 1200, or empty. There is no in-between value
  // the server will store, which is why the form keeps an unsent draft in this browser instead.
  const bio = draft.bioLong;
  if (bio.length > 0) {
    const count = graphemeLength(bio);
    if (count < LIMITS.bioLong.min) {
      put('bioLong', t('validation.bio.tooShort', { count, min: LIMITS.bioLong.min }));
    } else if (count > LIMITS.bioLong.max) {
      put('bioLong', t('validation.bio.tooLong', { count, max: LIMITS.bioLong.max }));
    } else if (codePointLength(bio) > LIMITS.bioLong.max) {
      put('bioLong', t('validation.bio.storedAsSeveral'));
    }
  }

  // Design 25: a place is picked, never typed; the years are a whole number in their bound.
  if (locationToSend(draft.primaryLocation) === undefined) {
    put('primaryLocation', t('validation.primaryLocation'));
  }
  const years = draft.yearsOfPractice.trim();
  if (
    years !== '' &&
    (!/^\d{1,3}$/.test(years) ||
      Number(years) < YEARS_OF_PRACTICE.min ||
      Number(years) > YEARS_OF_PRACTICE.max)
  ) {
    put(
      'yearsOfPractice',
      t('validation.yearsOfPractice', { min: YEARS_OF_PRACTICE.min, max: YEARS_OF_PRACTICE.max }),
    );
  }

  draft.tags.forEach((tag, index) => {
    const count = graphemeLength(tag.tagText);
    if (count < LIMITS.tagText.min || count > LIMITS.tagText.max) {
      put(
        `tags.${index}.tagText`,
        t('validation.tagLength', { min: LIMITS.tagText.min, max: LIMITS.tagText.max }),
      );
    }
  });
  // No count refusal: the topics field stops a ninth itself, and an author who already holds more
  // keeps them (`TOPICS_MAX`, ./limits.ts; design 24, T25).

  const seenKinds = new Set<string>();
  draft.links.forEach((link, index) => {
    if (seenKinds.has(link.linkKind)) {
      // One link per kind per author is an INVARIANT rather than a convention.
      put(`links.${index}.linkKind`, t('validation.links.kindTaken'));
    }
    seenKinds.add(link.linkKind);
    if (link.url.length < LIMITS.linkUrl.min || link.url.length > LIMITS.linkUrl.max) {
      put(
        `links.${index}.url`,
        t('validation.links.length', { min: LIMITS.linkUrl.min, max: LIMITS.linkUrl.max }),
      );
    } else if (!/^https?:\/\//i.test(link.url)) {
      // The 422 names "a link scheme outside the allowlist"; the allowlist itself is not
      // published, so this mirror only refuses what is plainly not a web address.
      put(`links.${index}.url`, t('validation.links.scheme'));
    }
  });

  return errors;
}

/**
 * Merges a server 422 onto the field map. The platform returns the WHOLE failure set rather than
 * the first failure alone, so everything is rendered at once with a focusable summary above it.
 * Where the server does not name a field, the caller shows the message at the top of the form.
 */
/** What a length refusal calls each field, as `application` catalogue keys. */
const LENGTH_LABEL = {
  headline: 'validation.label.headline',
  bioShort: 'validation.label.bioShort',
  about: 'validation.label.about',
  experience: 'validation.label.experience',
  approach: 'validation.label.approach',
  education: 'validation.label.education',
  pronouns: 'validation.label.pronouns',
  privateEmail: 'validation.label.privateEmail',
  availabilityNote: 'validation.label.availabilityNote',
} as const;

export function mergeServerErrors(local: FieldErrors, server: FieldErrors): FieldErrors {
  return { ...local, ...server };
}

/**
 * ★ WHAT THE DRAFT TAKES FROM A SAVE'S ANSWER - every key the save SENT, and every key the person
 * has NOT EDITED since the answer before it (the draft still equals the old baseline there). A key
 * they have edited and not yet saved keeps their text: a save in one section never reverts typing
 * in another.
 *
 * WHY NOT ONLY THE KEYS IT SENT, which is what the hook did until design 14 (writer W6's find).
 * The server can answer with more than it was sent: on a person's first profile save, when no
 * private row exists yet, it fills the contact e-mail from the verified sign-in e-mail
 * (`ProfileService.updateOwnProfile` -> `ProfileRepository.seedContactEmail`; it never overwrites
 * a value). Taking back only the sent keys left the baseline holding that address and the draft
 * holding '' - so the contact field stayed empty until a reload, the private section claimed
 * unsaved changes nobody had made, and saving it sent `private.email: null` and CLEARED the
 * address the server had just filled. Under this rule the field shows the address at once, and an
 * untouched section has nothing to send.
 */
export function adoptAnswer(
  current: ApplicationDraft,
  oldBaseline: ApplicationDraft,
  answer: ApplicationDraft,
  sent: readonly DraftKey[],
): ApplicationDraft {
  const merged: ApplicationDraft = { ...current };
  for (const key of Object.keys(answer) as DraftKey[]) {
    if (sent.includes(key) || isUnchanged(current, oldBaseline, [key])) {
      Object.assign(merged, { [key]: answer[key] });
    }
  }
  return merged;
}

/* ============================================ design 25's primary location, at the foot */

function locationDraftOf(onFile: OwnProfile['primaryLocation']): PrimaryLocationDraft {
  if (onFile === null || onFile === undefined) {
    return { text: '', picked: null };
  }
  // A place on file with no zone is shown, and is sent again only once it is picked again.
  return {
    text: onFile.label,
    picked:
      onFile.timeZone === null
        ? null
        : { placeId: onFile.placeId, label: onFile.label, timeZone: onFile.timeZone },
  };
}

/**
 * What the box asks to send: the pick, `null` for an empty box (clear it), or `undefined` for text
 * that is not the pick's own label - which is nothing to send, and a refusal on screen.
 */
function locationToSend(draft: PrimaryLocationDraft): PrimaryLocation | null | undefined {
  if (draft.text.trim() === '') {
    return null;
  }
  return draft.picked !== null && draft.picked.label === draft.text ? draft.picked : undefined;
}

function sameLocation(a: PrimaryLocation | null, b: PrimaryLocation | null | undefined): boolean {
  return JSON.stringify(a) === JSON.stringify(b ?? null);
}
